import { isEmpty } from "lodash";
import { Fragment, useEffect, useRef } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { CircularProgress } from "@/Components/v2/atoms/Progress/CircularProgress";
import { Stack } from "@/Components/v2/atoms/Stack";
import NoData from "@/Components/v2/molecule/NoData";
import Header from "@/Components/v2/organism/Header/Header";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useConfig } from "@/hooks/useConfig";
import { Toggle, ToolBarShared } from "@/shared/components/DataGrid/ToolBarShared";
import DataGridContainer from "@/shared/components/DataGridContainer";
import { useDateRangeStore } from "@/shared/components/date-range/store/useDateRangeStore";
import Summary from "@/shared/components/Sections/Summary";
import Title from "@/shared/components/Sections/Title";
import { useMetricOptions } from "@/shared/hooks/useMetricOptions";
import { Layer } from "@/Utils/hierarchy";
import { BusinessUnitToggle, GeoViewToggle, MetricType, ViewToggle } from "@/Utils/sharedTypes";

import { useCampaignsROIsV3 } from "./api/getCampaignsROIs";
import BreakdownV2 from "./components/Sections/BreakdownV2";
import { content, noDataContent } from "./constants/content";
import { DEFAULT_COLUMNS } from "./constants/defaults";
import { getColumnDefinitions } from "./getColumnDefinitions";
import { useCampaignsStore } from "./store/useCampaignsStore";
import { useAvailableFilters } from "./useAvailableFilters";

interface CampaignsProps {}

const Campaigns: React.FC<CampaignsProps> = () => {
  const { config } = useConfig();
  const { nodes } = useInfo();
  const { startDate, endDate } = useDateRangeStore();
  const { campaigns, channels, targetNode, geographies } = useCampaignsStore(
    (state) => state.filters
  );
  const {
    setTargetNode,
    setActiveColumns,
    setGridView,
    setGeoView,
    setBusinessUnitToggle,
    setMetricType,
  } = useCampaignsStore();
  const { geoView, gridView, activeColumns, businessUnitToggle, metricType } = useCampaignsStore(
    (state) => state.grid
  );
  const { chartView } = useCampaignsStore((state) => state.chart);
  const { setCampaigns, setChannels, setChartView } = useCampaignsStore();
  const apiRef = useRef(null);
  const metricOptions = useMetricOptions();

  const currencySymbol = config.currency.getSymbol();
  const formatCurrency = config.currency.formatCurrency;

  useEffect(() => {
    if (nodes && targetNode === null) {
      const defaultNodeUUID = nodes?.getDefaultNode()?.node.uuid;
      if (defaultNodeUUID) {
        setTargetNode(defaultNodeUUID);
      }
    }
  }, [targetNode, nodes, setTargetNode]);

  const selectedNode = nodes?.getNode(targetNode || "");

  const { data: campaignROIsV3, isLoading: isCampaignsLoading } = useCampaignsROIsV3({
    brand: selectedNode?.node.brand ?? "default",
    category: selectedNode?.node.category ?? "default",
    product: selectedNode?.node.product ?? "default",
    channels: channels,
    startDate: startDate ?? "",
    endDate: endDate ?? "",
    graphView: chartView,
    tableView: gridView,
    geoViewToggle: geoView,
    businessUnitToggle: businessUnitToggle,
    campaigns,
    geographies: geographies,
    config: {
      enabled:
        !!selectedNode &&
        !!startDate &&
        !!endDate &&
        !!chartView &&
        !!gridView &&
        !!businessUnitToggle,
    },
  });

  const columns = getColumnDefinitions(activeColumns, {
    currencySymbol,
    formatCurrency,
    geoView,
    gridView,
    businessUnitToggle,
    metricType,
    config,
  });

  const availableFilters = useAvailableFilters({
    selectedNode,
    startDate: startDate ?? "",
    endDate: endDate ?? "",
  });

  const { loading: areGeosLoading, initInfo } = useInfo();
  const { moduleOpened } = useAnalytics();

  useEffect(() => {
    moduleOpened("campaigns", Layer.Portfolio);
  }, []);

  const showWelcomeScreen = !campaigns || campaigns.length === 0 || channels.length === 0;

  const isLoading = !nodes || isCampaignsLoading || areGeosLoading;

  const toggles: Toggle[] = [
    {
      id: "viewToggle",
      selectedValue: gridView,
      options: [
        { label: "Campaigns", value: ViewToggle.INSIGHT },
        { label: "Channels", value: ViewToggle.CHANNEL },
      ],
    },
    {
      id: "businessUnitToggle",
      selectedValue: businessUnitToggle,
      options: [
        { label: "Aggregate Business Units", value: "aggregate" },
        { label: "Split by Business Unit", value: "split" },
      ],
    },
  ];

  const metricDropdownConfig = {
    label: "Show",
    value: metricType,
    options: metricOptions,
    onChange: (value: string | null) => {
      if (value) {
        setMetricType(value as MetricType);
      }
    },
  };

  const [geoData] = initInfo?.geographies || [];

  if (geoData && geoData.length > 0) {
    toggles.push({
      id: "geoViewToggle",
      selectedValue: geoView,
      options: [
        { label: "Aggregate Geographies", value: GeoViewToggle.AGGREGATE },
        { label: "Split by Geography", value: GeoViewToggle.SPLIT },
      ],
    });
  }

  return (
    <Box>
      <Title title={content.title} subtitle={content.subtitle} />
      <Header availableFilters={availableFilters} />
      {isLoading ? (
        <Stack justifyContent="center" alignItems="center" sx={{ minHeight: "50rem" }}>
          <CircularProgress size={150} />
        </Stack>
      ) : isEmpty(campaignROIsV3?.chartView) || isEmpty(campaignROIsV3?.totals) ? (
        <NoData title={noDataContent.title} description={noDataContent.description} />
      ) : (
        <Fragment>
          <Box sx={{ marginX: 4 }}>
            <Summary
              spend={campaignROIsV3?.totals.totalSpend ?? 0}
              revenue={campaignROIsV3?.totals.totalRevenue ?? 0}
              roi={campaignROIsV3?.totals.totalRoi ?? 0}
              formatCurrency={formatCurrency}
            />
            <BreakdownV2
              title={content.filterLabel}
              showWelcomeScreen={showWelcomeScreen}
              data={campaignROIsV3?.chartView ?? []}
              totalSpend={campaignROIsV3?.totals.totalSpend ?? 0}
              totalRevenue={campaignROIsV3?.totals.totalRevenue ?? 0}
              currencySymbol={currencySymbol}
              setChannels={setChannels}
              setCampaigns={setCampaigns}
              setChartView={setChartView}
            />
            <DataGridContainer
              apiRef={apiRef}
              rows={campaignROIsV3?.tableView ?? []}
              columns={columns}
              metricDropdown={metricDropdownConfig}
              toolbarComponent={
                <ToolBarShared
                  apiRef={apiRef}
                  columns={columns}
                  activeColumns={activeColumns}
                  toggles={toggles}
                  handleApplyFilters={({ activeColumns, toggles }) => {
                    const gridViewToggle = toggles["viewToggle"];
                    const geoViewToggle = toggles["geoViewToggle"];
                    const businessUnitToggle = toggles["businessUnitToggle"];
                    setActiveColumns(activeColumns);
                    setGridView(gridViewToggle as ViewToggle);

                    if (geoViewToggle) {
                      setGeoView(geoViewToggle as GeoViewToggle);
                    }

                    if (businessUnitToggle) {
                      setBusinessUnitToggle(businessUnitToggle as BusinessUnitToggle);
                    }
                  }}
                  handleResetFilters={() => {
                    setActiveColumns(DEFAULT_COLUMNS);
                    setGridView(ViewToggle.INSIGHT);
                    setGeoView(GeoViewToggle.AGGREGATE);
                    setMetricType(MetricType.MARKETING_ROI);
                    setBusinessUnitToggle(BusinessUnitToggle.AGGREGATE);
                  }}
                />
              }
              options={{
                rowHeight: 52,
                sortModel: [{ field: "total_spend", sort: "desc" }],
              }}
            />
          </Box>
        </Fragment>
      )}
    </Box>
  );
};

export default Campaigns;
